import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { History, Play, X } from "lucide-react";

const DAY_LABELS = {
  1: "Day 1 — Monday 22 June",
  2: "Day 2 — Tuesday 23 June",
  3: "Day 3 — Wednesday 24 June",
};

/**
 * "Watch back" — surfaces admin-curated recordings of completed live streams.
 * Hidden entirely until at least one published replay exists.
 */
export default function ReplaysPanel() {
  const [replays, setReplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // replay being played in modal

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/conference/replays");
        if (!cancelled) setReplays(r.data?.replays || []);
      } catch {
        if (!cancelled) setReplays([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!replays.length) return null;

  // Group by day_index.
  const byDay = new Map();
  for (const r of replays) {
    if (!byDay.has(r.day_index)) byDay.set(r.day_index, []);
    byDay.get(r.day_index).push(r);
  }
  const days = [...byDay.keys()].sort();

  return (
    <section id="replays" className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-8" data-testid="replays-panel">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Catch-up</div>
          <h3 className="mt-1 font-heading text-xl sm:text-2xl text-slate-900 font-medium tracking-tight">
            Watch back
          </h3>
          <p className="text-sm text-slate-500 mt-1">Recordings of completed live streams. Tap to play.</p>
        </div>
        <div className="h-10 w-10 shrink-0 rounded-full bg-[#28BBE3]/10 flex items-center justify-center">
          <History className="text-[#28BBE3]" size={18} strokeWidth={1.6} />
        </div>
      </div>

      <div className="space-y-6">
        {days.map((di) => (
          <div key={di}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-3">
              {DAY_LABELS[di] || `Day ${di}`}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {byDay.get(di).map((r) => (
                <button
                  key={r.replay_id}
                  type="button"
                  onClick={() => setActive(r)}
                  className="text-left rounded-2xl ring-1 ring-black/5 bg-slate-50 hover:bg-white hover:ring-[#28BBE3]/40 transition-all p-4 group"
                  data-testid={`replay-card-${r.replay_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 shrink-0 rounded-full bg-[#28BBE3] flex items-center justify-center group-hover:scale-105 transition-transform">
                      <Play size={14} strokeWidth={2.4} className="text-white ml-0.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 leading-snug">{r.title}</div>
                      {r.description && (
                        <div className="text-xs text-slate-500 mt-1 leading-relaxed">{r.description}</div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {active && (
        <ReplayModal replay={active} onClose={() => setActive(null)} />
      )}
    </section>
  );
}

function ReplayModal({ replay, onClose }) {
  // Close on Escape.
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="replay-modal"
    >
      <div
        className="w-full max-w-5xl bg-[#0F172A] rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-semibold">{DAY_LABELS[replay.day_index] || `Day ${replay.day_index}`}</div>
            <div className="font-medium text-white truncate">{replay.title}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center text-white"
            data-testid="replay-modal-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="relative bg-black" style={{ paddingTop: "56.25%" }}>
          <iframe
            src={replay.embed_url || replay.url}
            title={replay.title}
            className="absolute inset-0 w-full h-full border-0"
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
        {replay.description && (
          <div className="px-5 py-4 text-sm text-white/70">{replay.description}</div>
        )}
      </div>
    </div>
  );
}
